import { useState, useEffect } from "react";
import { ArrowRight } from "lucide-react";
import { LANDING } from "@/constants/testIds";
import DemoRequestModal from "@/components/DemoRequestModal";

const SERIF = "font-['Playfair_Display']";
const SANS = "font-['DM_Sans']";

const IMG = {
  colonnade:
    "https://images.unsplash.com/photo-1586073054612-fdd6537fc6d4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  paper:
    "https://images.unsplash.com/photo-1601662528567-526cd06f6582?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
};

const NAV = [
  { label: "How Artisan Works", href: "#how" },
  { label: "Beyond Templates", href: "#beyond" },
];

const BELIEFS = [
  {
    n: "01",
    text: "Flexible Brand Rules are more powerful than rigid templates.",
  },
  {
    n: "02",
    text: "Local marketers should be free to define their marketing problem, without being forced to invent the solution.",
  },
  {
    n: "03",
    text: "Every local execution is a laboratory for continuous learning and improvement.",
  },
];

const STEPS = [
  {
    k: "Govern",
    h: "Author rules, not templates",
    p: "Capture brand voice, asset bindings, and production constraints as living rules. Artisan stores them structurally and enforces them at every step.",
  },
  {
    k: "Assemble",
    h: "Governed AI does the work",
    p: "Local marketers describe the problem. The Assembly Agent retrieves, reasons, and composes within your rules, permissions, and campaign brief.",
  },
  {
    k: "Optimize",
    h: "Every execution teaches the system",
    p: "Approvals, compliance scores, and outcomes feed back in. The next execution starts smarter than the last — production that compounds.",
  },
];

export default function Landing() {
  const [demoOpen, setDemoOpen] = useState(false);
  const authError =
    typeof window !== "undefined" && window.location.search.includes("auth_error=1");

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleSignIn = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(
      redirectUrl,
    )}`;
  };

  useEffect(() => {
    const els = document.querySelectorAll("[data-reveal]");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("reveal-in");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 },
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <div className={`min-h-screen bg-[#F8F5EE] text-[#1A1F2C] ${SANS}`}>
      <style>{`
        [data-reveal]{opacity:0;transform:translateY(18px);transition:opacity .9s cubic-bezier(.22,1,.36,1),transform .9s cubic-bezier(.22,1,.36,1);}
        [data-reveal].reveal-in{opacity:1;transform:none;}
        html{scroll-behavior:smooth;}
        ::selection{background:#B5462F;color:#fff;}
      `}</style>

      {/* NAV */}
      <header className="fixed top-0 inset-x-0 z-50 bg-[#F8F5EE]/90 backdrop-blur-md border-b border-[#E5DFD3]">
        <nav className="max-w-7xl mx-auto px-6 md:px-12 h-[68px] flex items-center justify-between">
          <a href="#top" className="flex items-baseline gap-2">
            <span className="w-2.5 h-2.5 rounded-sm bg-[#B5462F] inline-block translate-y-[-1px]" />
            <span className={`${SERIF} text-2xl tracking-tight`}>Artisan</span>
            <span className="text-[10px] uppercase tracking-[0.22em] text-[#6B6358]">by Pica9</span>
          </a>

          <div className="hidden lg:flex items-center gap-9">
            {NAV.map((n) => (
              <a
                key={n.href}
                href={n.href}
                className="text-sm text-[#6B6358] hover:text-[#1A1F2C] transition-colors"
              >
                {n.label}
              </a>
            ))}
          </div>

          <div className="flex items-center gap-2 sm:gap-4">
            <button
              data-testid={LANDING.requestDemoNav}
              onClick={() => setDemoOpen(true)}
              className="hidden sm:inline-flex text-sm text-[#1A1F2C] border border-[#1A1F2C] px-5 py-2.5 hover:bg-[#1A1F2C] hover:text-[#F8F5EE] transition-colors tracking-wide"
            >
              Request a Demo
            </button>
            <button
              data-testid={LANDING.signInButton}
              onClick={handleSignIn}
              className="text-sm text-[#6B6358] hover:text-[#1A1F2C] transition-colors px-2 py-2"
            >
              Login
            </button>
          </div>
        </nav>
      </header>

      {/* HERO */}
      <section id="top" className="relative pt-[68px] overflow-hidden">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.12] mix-blend-multiply"
          style={{
            backgroundImage: `url(${IMG.paper})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-24 md:pb-32">
          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8" data-reveal>
              <p className="text-[11px] uppercase tracking-[0.26em] text-[#B5462F] mb-7">
                Brand-to-local marketing, governed by AI
              </p>
              <h1 className={`${SERIF} text-[2.6rem] sm:text-6xl md:text-7xl leading-[1.04] tracking-tight font-medium`}>
                Because winning brands
                <br className="hidden sm:block" /> do more than endure.
                <br className="hidden sm:block" />
                <span className="italic text-[#B5462F]"> They evolve.</span>
              </h1>
            </div>
            <div className="lg:col-span-4 lg:pb-3" data-reveal>
              <p className="text-lg leading-relaxed text-[#3D3A35]">
                Artisan is a new kind of brand-to-local marketing system that uses governed AI to
                help brands adapt locally while preserving the rules, voice, and creative intent
                that make them unmistakable.
              </p>
              <div className="mt-8 flex items-center gap-5">
                <button
                  data-testid={LANDING.requestDemoHero}
                  onClick={() => setDemoOpen(true)}
                  className="group inline-flex items-center gap-2 bg-[#B5462F] text-white px-7 py-4 hover:bg-[#9A3B27] transition-colors tracking-wide text-sm font-medium"
                >
                  Request a Demo
                  <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </button>
                <a href="#how" className="text-sm text-[#6B6358] hover:text-[#1A1F2C] transition-colors">
                  How it works
                </a>
              </div>
            </div>
          </div>

          {authError && (
            <div className="mt-12 border border-[#B5462F]/40 bg-[#B5462F]/5 p-4 max-w-xl">
              <p className="text-sm text-[#B5462F]">
                <strong>Sign-in didn&rsquo;t complete.</strong> Please try logging in again to mint a
                fresh session.
              </p>
            </div>
          )}
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="border-t border-[#E5DFD3]" />
        </div>
      </section>

      {/* HOW ARTISAN WORKS */}
      <section id="how" className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4" data-reveal>
            <p className="text-[11px] uppercase tracking-[0.26em] text-[#B5462F] mb-5">How Artisan works</p>
            <h2 className={`${SERIF} text-3xl md:text-5xl tracking-tight leading-tight font-medium`}>
              Govern. Assemble. Optimize.
            </h2>
            <p className="text-[#6B6358] mt-5 leading-relaxed">
              One closed loop — from brand rules to a print-ready local execution, getting smarter
              every cycle.
            </p>
          </div>
          <div className="lg:col-span-8 grid sm:grid-cols-3 gap-px bg-[#E5DFD3] border border-[#E5DFD3]">
            {STEPS.map((s, i) => (
              <div key={s.k} className="bg-[#F8F5EE] p-8 md:p-9" data-reveal style={{ transitionDelay: `${i * 90}ms` }}>
                <span className={`${SERIF} text-[#B5462F] text-2xl`}>0{i + 1}</span>
                <p className="text-[11px] uppercase tracking-[0.2em] text-[#6B6358] mt-4">{s.k}</p>
                <h3 className={`${SERIF} text-2xl mt-1.5 tracking-tight`}>{s.h}</h3>
                <p className="text-sm text-[#3D3A35] mt-3 leading-relaxed">{s.p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BEYOND TEMPLATES / CORE BELIEFS */}
      <section id="beyond" className="bg-[#F0EBE1] border-y border-[#E5DFD3]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5" data-reveal>
            <p className="text-[11px] uppercase tracking-[0.26em] text-[#B5462F] mb-5">Beyond templates</p>
            <h2 className={`${SERIF} text-3xl md:text-5xl tracking-tight leading-tight font-medium`}>
              Artisan is built around three core beliefs.
            </h2>
            <div className="mt-9 hidden lg:block overflow-hidden border border-[#E5DFD3]">
              <img
                src={IMG.colonnade}
                alt="Ordered colonnade — structure that gives freedom"
                className="w-full h-64 object-cover hover:scale-[1.03] transition-transform duration-700"
                loading="lazy"
              />
            </div>
          </div>
          <div className="lg:col-span-7 space-y-px bg-[#E5DFD3] border border-[#E5DFD3]">
            {BELIEFS.map((b, i) => (
              <div
                key={b.n}
                className="bg-[#F8F5EE] p-8 md:p-10 flex gap-7 items-start"
                data-reveal
                style={{ transitionDelay: `${i * 90}ms` }}
              >
                <span className={`${SERIF} text-4xl md:text-5xl text-[#B5462F] leading-none`}>{b.n}</span>
                <p className={`${SERIF} text-xl md:text-2xl leading-snug tracking-tight text-[#1A1F2C]`}>
                  {b.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CLOSING VALUE BAND */}
      <section className="bg-[#1A1F2C] text-[#F8F5EE]">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-28 md:py-36 text-center" data-reveal>
          <p className="text-[11px] uppercase tracking-[0.26em] text-[#E08A6F] mb-8">
            Tighter compliance &middot; Efficient production &middot; Optimized over time
          </p>
          <h2 className={`${SERIF} text-3xl sm:text-4xl md:text-[3.4rem] leading-[1.35] sm:leading-[1.4] tracking-tight font-medium`}>
            Tighter brand compliance. More efficient creative-production. Delivered through a system
            that optimizes executions over time.
          </h2>
          <p className="text-lg text-[#C9C3B7] mt-8 max-w-2xl mx-auto leading-relaxed">
            Find out how Artisan can help drive better local marketing. Request a demo today.
          </p>
          <button
            data-testid={LANDING.requestDemoClosing}
            onClick={() => setDemoOpen(true)}
            className="group inline-flex items-center gap-2 bg-[#B5462F] text-white px-9 py-4 mt-11 hover:bg-[#9A3B27] transition-colors tracking-wide text-sm font-medium"
          >
            Request a Demo
            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#1A1F2C] border-t border-white/10 text-[#9A938A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-baseline gap-2">
            <span className={`${SERIF} text-xl text-[#F8F5EE]`}>Artisan</span>
            <span className="text-[10px] uppercase tracking-[0.22em]">by Pica9</span>
          </div>
          <div className="flex items-center gap-7 text-xs">
            {NAV.map((n) => (
              <a key={n.href} href={n.href} className="hover:text-[#F8F5EE] transition-colors">
                {n.label}
              </a>
            ))}
            <button onClick={handleSignIn} className="hover:text-[#F8F5EE] transition-colors">Login</button>
          </div>
          <span className="text-xs">&copy; {new Date().getFullYear()} Pica9</span>
        </div>
      </footer>

      {demoOpen && <DemoRequestModal onClose={() => setDemoOpen(false)} />}
    </div>
  );
}
